import { FlowManager, useFlowContext, useFlowControls } from '@getvim/flow-manager';
import { Login } from '@getvim/vim-connect';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { seamlessApiClient } from '../api/seamlessApiClient';
import { BackButton } from '../atomic/atoms/back-button';
import {
  analyticsClient,
  LoginMethods,
  VimConnectCredentialsLoginAnalyticsEventTypes,
  VimConnectOtpLoginAnalyticsEventTypes,
} from '../logic/analytics';
import { loginWidgetLogger } from '../logic/logger';
import { close, sendAccessToken, sendLoginFailure, sendUserLoginAttempt } from '../logic/messaging';
import { AccessToken, LoginResult, OtpRequestInput } from '../logic/types';
import {
  ContactDetail,
  ContactDetailType,
  Credentials,
  CredentialsMetadata,
  EhrCredentials,
  LoginStep,
  LoginSteps,
} from '../types';
import CredentialsForm from './CredentialsForm';
import { FailureScreen } from './failure-screen';
import { EnterOtpCode } from './otp-login/EnterOtpCode';
import { ErrorPage } from './otp-login/error-page';
import { OtpLoginPage } from './otp-login/OtpLogin';
import { RequestOtpPage } from './otp-login/request-otp';
import { PasswordExpiredForm } from './password-expired/PasswordExpiredForm';
import { PasswordResetForm } from './password-reset/PasswordResetFormNew';
import { SeamlessLoginFailed } from './seamless-login';

interface LoginContext {
  accessToken: string;
  refreshToken: string;
  vimCredentials?: Credentials;
}

interface LoginFlowManagerProps {
  ehrCredentials: EhrCredentials;
  credentialsMetadata: CredentialsMetadata | undefined;
  organizationId: number;
  contactDetails: ContactDetail[];
  isSeamlessLoginFailed?: boolean;
  wasSeamlessLoginShownAndDenied?: boolean;
  goBackToSeamlessLogin: () => void;
}

export const useLoginContext = () => useFlowContext<LoginContext>();
export const useLoginControls = () => useFlowControls<LoginSteps>();

export const LoginFlowManager: FC<LoginFlowManagerProps> = ({
  ehrCredentials: { linkedEhrUsername, ...ehrCredentials },
  credentialsMetadata,
  organizationId,
  contactDetails,
  isSeamlessLoginFailed,
  wasSeamlessLoginShownAndDenied,
  goBackToSeamlessLogin,
}) => {
  const [isOtpFlow] = useState<boolean>(contactDetails.length > 0);

  const [isSendOtpLoading, setIsSendOtpLoading] = useState(false);

  const [, setOtpRequestCodeAttemptCounter] = useState<number>(0);
  const [, setOtpSendCodeAttemptCounter] = useState<number>(0);
  const [otpSubmitCodeAttemptCounter, setOtpSubmitCodeAttemptCounter] = useState<number>(0);
  const [credentialsLoginAttemptCounter, setCredentialsLoginAttemptCounter] = useState<number>(0);

  const alreadySentAccessTokenRef = useRef(false);
  const contactDetailsRef = useRef(contactDetails);
  const onRequestOtpCodeRef = useRef<
    ((contactDetailType: ContactDetailType) => Promise<boolean>) | null
  >(null);

  useEffect(() => {
    contactDetailsRef.current = contactDetails;
  }, [contactDetails]);

  const onRequestOtpCode = useCallback(
    async (contactDetailType: ContactDetailType): Promise<boolean> => {
      const input: OtpRequestInput = {
        ehrUser: linkedEhrUsername,
        organizationId,
        contactDetailType,
      };

      let isSuccess: boolean;

      try {
        // TODO: original implementation doesn't show errors, send otp button is just enabled again
        setIsSendOtpLoading(true);
        const otpResponse = await seamlessApiClient.sendOtp(input);
        isSuccess = otpResponse.success;
      } finally {
        setIsSendOtpLoading(false);
      }

      setOtpSendCodeAttemptCounter((prev) => {
        const attemptCounter = prev + 1;
        analyticsClient.track(
          VimConnectOtpLoginAnalyticsEventTypes.VimConnectOtpSendCodeButtonClicked,
          {
            method: contactDetailType,
            is_success: isSuccess,
            attempt_number: attemptCounter,
          },
        );
        return attemptCounter;
      });

      return isSuccess;
    },
    [linkedEhrUsername, organizationId],
  );

  onRequestOtpCodeRef.current = onRequestOtpCode;

  return (
    <div>
      <FlowManager<LoginContext, LoginSteps>
        initialContext={{}}
        initialStep={isOtpFlow ? LoginStep.OTP_LOGIN : LoginStep.CREDENTIALS_FORM}
        steps={{
          CREDENTIALS_FORM: () => {
            const { updateContext, context } = useLoginContext();
            const { next } = useLoginControls();

            useEffect(() => {
              analyticsClient.track(
                VimConnectOtpLoginAnalyticsEventTypes.VimConnectLoginDisplayed,
                {
                  is_otp_displayed: false,
                },
              );
            }, []);

            const handleSuccess = useCallback(
              ({ accessToken: { accessToken, refreshToken } }: LoginResult) => {
                updateContext({ accessToken, refreshToken });
                analyticsClient.track(
                  VimConnectOtpLoginAnalyticsEventTypes.VimConnectInitialSuccessLogin,
                  {
                    login_type: LoginMethods.credentials,
                  },
                );
                loginWidgetLogger.info('Vim Connect initial successful login', {
                  login_type: LoginMethods.credentials,
                });
                next(LoginStep.SUCCESS_LOGIN);
              },
              [updateContext, next],
            );

            const handleFailure = useCallback(
              (error: Error) => {
                sendLoginFailure(error);
                next(LoginStep.FAILURE);
              },
              [next],
            );

            const handlePasswordExpired = useCallback(
              (vimCredentials: Credentials) => {
                updateContext({ ...context, vimCredentials });
                next(LoginStep.EXPIRED_PASSWORD_CHANGE);
              },
              [updateContext, context, next],
            );

            const handlePasswordResetRequest = useCallback(() => {
              const userHasEmail = contactDetailsRef.current.some(
                (contactDetail) => contactDetail.type === ContactDetailType.email,
              );
              analyticsClient.track(
                VimConnectCredentialsLoginAnalyticsEventTypes.VimConnectCredentialsForgotPasswordButtonClicked,
                {
                  user_has_email: userHasEmail,
                },
              );

              next(LoginStep.PASSWORD_RESET_REQUEST_FORM);
            }, [next]);

            return (
              <>
                {wasSeamlessLoginShownAndDenied && <BackButton onBack={goBackToSeamlessLogin} />}
                <div className={isSeamlessLoginFailed ? '' : 'padding-top-40'}>
                  {isSeamlessLoginFailed || credentialsMetadata?.shouldShowCredentialsWithError ? (
                    <SeamlessLoginFailed />
                  ) : null}
                  <CredentialsForm
                    inputStyle="large"
                    shouldShowTitle
                    ehrCredentials={ehrCredentials}
                    organizationId={organizationId}
                    onSuccess={handleSuccess}
                    onFailure={handleFailure}
                    credentialsLoginAttemptCounter={credentialsLoginAttemptCounter}
                    setCredentialsLoginAttemptCounter={setCredentialsLoginAttemptCounter}
                    onPasswordExpired={handlePasswordExpired}
                    onPasswordResetRequest={handlePasswordResetRequest}
                    usernamePlaceholder={linkedEhrUsername ?? 'Username'}
                    usernameLabel="Username"
                    passwordPlaceholder="Password"
                    passwordLabel="Vim password"
                  />
                </div>
              </>
            );
          },
          FAILURE: FailureScreen,
          SUCCESS_LOGIN: () => {
            const { context } = useLoginContext();
            const { accessToken, refreshToken } = context;

            useEffect(() => {
              /* TODO: There is bug that cause the LoginFlowManager to render again, ending up the sendAccessToken twice */
              if (alreadySentAccessTokenRef.current) {
                return;
              }

              sendAccessToken(accessToken, refreshToken);
              alreadySentAccessTokenRef.current = true;
              // eslint-disable-next-line react-hooks/exhaustive-deps
            }, []);
            useEffect(() => {
              close();
            }, []);

            return null;
          },
          PASSWORD_RESET_REQUEST_FORM: () => {
            const { prev } = useLoginControls();

            return (
              <PasswordResetForm
                exitForm={prev}
                contactDetails={contactDetails}
                username={linkedEhrUsername}
                organizationId={organizationId}
              />
            );
          },
          EXPIRED_PASSWORD_CHANGE: () => {
            const { updateContext, context } = useLoginContext();
            const { next } = useLoginControls();

            const handleExpiredPasswordSuccess = useCallback(
              ({ accessToken: { accessToken, refreshToken } }: LoginResult) => {
                sendUserLoginAttempt(Login.LoginMethodType.CREDENTIALS, { accessToken });
                updateContext({ accessToken, refreshToken });
                next(LoginStep.SUCCESS_LOGIN);
              },
              [updateContext, next],
            );

            const { vimCredentials } = context;

            return (
              <PasswordExpiredForm
                vimCredentials={vimCredentials ?? { username: '', password: '' }}
                ehrCredentials={ehrCredentials ?? { username: '', password: '' }}
                organizationId={organizationId}
                onSuccess={handleExpiredPasswordSuccess}
              />
            );
          },
          OTP_LOGIN: () => {
            const { updateContext, context } = useLoginContext();
            const { next } = useLoginControls();

            useEffect(() => {
              analyticsClient.track(
                VimConnectOtpLoginAnalyticsEventTypes.VimConnectLoginDisplayed,
                {
                  is_otp_displayed: true,
                },
              );
            }, []);

            const handleOtpCredsSuccess = useCallback(
              ({ accessToken: { accessToken, refreshToken } }: LoginResult) => {
                updateContext({ accessToken, refreshToken });
                analyticsClient.track(
                  VimConnectOtpLoginAnalyticsEventTypes.VimConnectInitialSuccessLogin,
                  {
                    login_type: LoginMethods.credentials,
                  },
                );
                loginWidgetLogger.info('Vim Connect initial successful login', {
                  login_type: LoginMethods.credentials,
                });
                next(LoginStep.SUCCESS_LOGIN);
              },
              [updateContext, next],
            );

            const handleOtpCredsFailure = useCallback(
              (error: Error) => {
                sendLoginFailure(error);
                next(LoginStep.FAILURE);
              },
              [next],
            );

            const handleOtpPasswordExpired = useCallback(
              (vimCredentials: Credentials) => {
                updateContext({ ...context, vimCredentials });
                next(LoginStep.EXPIRED_PASSWORD_CHANGE);
              },
              [updateContext, context, next],
            );

            const onPasswordReset = useCallback(() => {
              const userHasEmail = contactDetailsRef.current.some(
                (contactDetail) => contactDetail.type === ContactDetailType.email,
              );
              analyticsClient.track(
                VimConnectCredentialsLoginAnalyticsEventTypes.VimConnectCredentialsForgotPasswordButtonClicked,
                {
                  user_has_email: userHasEmail,
                },
              );
              next(LoginStep.PASSWORD_RESET_REQUEST_FORM);
            }, [next]);

            const onRequestOtp = useCallback(() => {
              setOtpRequestCodeAttemptCounter((prev) => {
                const attemptCounter = prev + 1;
                analyticsClient.track(
                  VimConnectOtpLoginAnalyticsEventTypes.VimConnectOtpRequestCodeButtonClicked,
                  {
                    attempt_number: attemptCounter,
                  },
                );
                return attemptCounter;
              });
              next(LoginStep.REQUEST_OTP);
            }, [next]);

            return (
              <OtpLoginPage
                ehrCredentials={ehrCredentials}
                credentialsMetadata={credentialsMetadata}
                ehrUsername={linkedEhrUsername}
                organizationId={organizationId}
                onCredsSuccess={handleOtpCredsSuccess}
                onCredsFailure={handleOtpCredsFailure}
                onPasswordExpired={handleOtpPasswordExpired}
                onPasswordResetRequest={onPasswordReset}
                onRequestOtp={onRequestOtp}
                isSeamlessLoginFailed={isSeamlessLoginFailed}
                wasSeamlessLoginShowedAndDenied={wasSeamlessLoginShownAndDenied}
                goBackToSeamlessLogin={goBackToSeamlessLogin}
                credentialsLoginAttemptCounter={credentialsLoginAttemptCounter}
                setCredentialsLoginAttemptCounter={setCredentialsLoginAttemptCounter}
              />
            );
          },
          REQUEST_OTP: () => {
            const { next, prev } = useLoginControls();

            const emailDetails = contactDetails?.find(
              (detail) => detail.type === ContactDetailType.email,
            );
            const phoneDetails = contactDetails?.find(
              (detail) => detail.type === ContactDetailType.phone,
            );

            const onSendOtpRequest = useCallback(
              async (contactDetailType: ContactDetailType): Promise<void> => {
                const requestOtpCode = onRequestOtpCodeRef.current;
                if (!requestOtpCode) return;
                const isSuccess = await requestOtpCode(contactDetailType);
                if (isSuccess) {
                  return next(
                    contactDetailType === ContactDetailType.email
                      ? LoginStep.CHECK_YOUR_EMAIL
                      : LoginStep.CHECK_YOUR_PHONE,
                  );
                }
                sendErrorPageAnalytic(ErrorReasonOptions.FAILED_TO_SEND_CODE);
                return next(LoginStep.OTP_FAILURE);
              },
              [next],
            );

            return (
              <RequestOtpPage
                userEmail={emailDetails?.hint}
                userPhoneNumber={phoneDetails?.hint}
                onBack={prev}
                isLoading={isSendOtpLoading}
                onSendOtpRequest={onSendOtpRequest}
              />
            );
          },
          CHECK_YOUR_EMAIL: () => {
            const { prev, next } = useLoginControls();
            const { updateContext } = useLoginContext();
            const emailDetails = contactDetails?.find(
              (detail) => detail.type === ContactDetailType.email,
            );

            const handleEmailSuccess = useCallback(
              ({ accessToken, refreshToken }: AccessToken) => {
                updateContext({ accessToken, refreshToken });
                next(LoginStep.SUCCESS_LOGIN);
              },
              [updateContext, next],
            );

            const handleEmailFailure = useCallback(() => {
              setOtpRequestCodeAttemptCounter(0);
              setOtpSendCodeAttemptCounter(0);
              setOtpSubmitCodeAttemptCounter(0);

              sendErrorPageAnalytic(ErrorReasonOptions.MAX_ATTEMPTS);
              next(LoginStep.OTP_FAILURE);
            }, [next]);

            const onResendOtpCode = useCallback(async () => {
              const requestOtpCode = onRequestOtpCodeRef.current;
              if (!requestOtpCode) return;
              const isSuccess = await requestOtpCode(ContactDetailType.email);
              if (!isSuccess) handleEmailFailure();
            }, [handleEmailFailure]);

            return (
              <EnterOtpCode
                onBack={prev}
                destinationHint={emailDetails?.hint ?? ''}
                contactDetailType={ContactDetailType.email}
                organizationId={organizationId}
                ehrUser={linkedEhrUsername}
                ehrCredentials={ehrCredentials}
                onSuccess={handleEmailSuccess}
                onFailure={handleEmailFailure}
                onResendPassword={onResendOtpCode}
                otpSubmitCodeAttemptCounter={otpSubmitCodeAttemptCounter}
                setOtpSubmitCodeAttemptCounter={setOtpSubmitCodeAttemptCounter}
              />
            );
          },
          CHECK_YOUR_PHONE: () => {
            const { prev, next } = useLoginControls();
            const { updateContext } = useLoginContext();
            const phoneDetails = contactDetails?.find(
              (detail) => detail.type === ContactDetailType.phone,
            );

            const handlePhoneSuccess = useCallback(
              ({ accessToken, refreshToken }: AccessToken) => {
                updateContext({ accessToken, refreshToken });
                next(LoginStep.SUCCESS_LOGIN);
              },
              [updateContext, next],
            );

            const handlePhoneFailure = useCallback(
              (error?) => {
                if (error) {
                  return sendLoginFailure(error);
                }
                sendErrorPageAnalytic(ErrorReasonOptions.MAX_ATTEMPTS);
                next(LoginStep.OTP_FAILURE);
              },
              [next],
            );

            const onResendOtpCode = useCallback(async () => {
              const requestOtpCode = onRequestOtpCodeRef.current;
              if (!requestOtpCode) return;
              const isSuccess = await requestOtpCode(ContactDetailType.phone);
              if (!isSuccess) handlePhoneFailure();
            }, [handlePhoneFailure]);

            return (
              <EnterOtpCode
                onBack={prev}
                destinationHint={phoneDetails?.hint ?? ''}
                contactDetailType={ContactDetailType.phone}
                organizationId={organizationId}
                ehrUser={linkedEhrUsername}
                ehrCredentials={ehrCredentials}
                onSuccess={handlePhoneSuccess}
                onFailure={handlePhoneFailure}
                onResendPassword={onResendOtpCode}
                otpSubmitCodeAttemptCounter={otpSubmitCodeAttemptCounter}
                setOtpSubmitCodeAttemptCounter={setOtpSubmitCodeAttemptCounter}
              />
            );
          },
          OTP_FAILURE: () => {
            const { next } = useLoginControls();
            const handleGoBack = useCallback(() => next(LoginStep.OTP_LOGIN), [next]);

            return <ErrorPage onGoBack={handleGoBack} />;
          },
        }}
      />
    </div>
  );
};

enum ErrorReasonOptions {
  MAX_ATTEMPTS = 'Max code attempts exceeded',
  FAILED_TO_SEND_CODE = 'Failed to send otp code',
}

const sendErrorPageAnalytic = (reason: string) => {
  analyticsClient.track(VimConnectOtpLoginAnalyticsEventTypes.VimConnectOtpErrorPageDisplayed, {
    reason,
  });
};
